export const ERROR_CODES = {
  OFFLINE: "OFFLINE",
  UNREACHABLE: "UNREACHABLE",
  CROSS_HOST_BLOCKED: "CROSS_HOST_BLOCKED",
  MIXED_CONTENT: "MIXED_CONTENT",
  HTTP_ERROR: "HTTP_ERROR",
  BACKEND_ERROR: "BACKEND_ERROR",
  UNKNOWN: "UNKNOWN",
};

const currentOrigin = () => {
  if (typeof window !== "undefined" && window.location) {
    return window.location.origin;
  }
  return undefined;
};

const currentProtocol = () => {
  if (typeof window !== "undefined" && window.location) {
    return window.location.protocol;
  }
  return undefined;
};

export const apiOrigin = (apiUrl) => {
  try {
    return new URL(apiUrl).origin;
  } catch (e) {
    return undefined;
  }
};

export const isCrossHost = (apiUrl) => {
  const origin = currentOrigin();
  const target = apiOrigin(apiUrl);
  if (!origin || !target) {
    return false;
  }
  return origin !== target;
};

export const isMixedContent = (apiUrl) => {
  if (currentProtocol() !== "https:") {
    return false;
  }
  try {
    return new URL(apiUrl).protocol === "http:";
  } catch (e) {
    return false;
  }
};

const isOffline = () => {
  return typeof navigator !== "undefined" && navigator.onLine === false;
};

// Network address spaces, ordered from most private (0) to most public (2).
// Chrome's Private Network Access only restricts requests that travel from a
// MORE public page to a MORE private target.
export const ADDRESS_SPACE = { LOCAL: 0, PRIVATE: 1, PUBLIC: 2 };

const isLoopbackHostname = (h) => {
  return h === "localhost" || h === "127.0.0.1" || h === "::1" || h === "[::1]" || h.endsWith(".localhost");
};

const isPrivateIPv4 = (h) => {
  const m = h.match(/^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/);
  if (!m) {
    return false;
  }
  const a = Number(m[1]);
  const b = Number(m[2]);
  if (a === 10) return true;                       // 10.0.0.0/8
  if (a === 172 && b >= 16 && b <= 31) return true; // 172.16.0.0/12
  if (a === 192 && b === 168) return true;          // 192.168.0.0/16
  if (a === 169 && b === 254) return true;          // 169.254.0.0/16 link-local
  return false;
};

const isPrivateHostname = (h) => {
  return isPrivateIPv4(h) || h.endsWith(".local") || /^\[?f[cd][0-9a-f]{2}:/i.test(h); // IPv6 ULA fc00::/7
};

export const addressSpace = (hostname) => {
  if (!hostname) {
    return ADDRESS_SPACE.PUBLIC;
  }
  const h = hostname.toLowerCase();
  if (isLoopbackHostname(h)) return ADDRESS_SPACE.LOCAL;
  if (isPrivateHostname(h)) return ADDRESS_SPACE.PRIVATE;
  return ADDRESS_SPACE.PUBLIC;
};

// True only when the page sits on a more public network than the target,
// i.e. the direction Chrome's Private Network Access actually blocks.
export const isPrivateNetworkRequest = (pageHostname, targetHostname) => {
  return addressSpace(pageHostname) > addressSpace(targetHostname);
};

const hostname = (url) => {
  try {
    return new URL(url).hostname;
  } catch (e) {
    return undefined;
  }
};

export const isPrivateNetworkAccess = (apiUrl) => {
  if (typeof window === "undefined" || !window.location) {
    return false;
  }
  return isPrivateNetworkRequest(window.location.hostname, hostname(apiUrl));
};

export const classifyError = ({ error, response, backendErrors, apiUrl }) => {
  const crossHost = isCrossHost(apiUrl);
  const base = { apiUrl, crossHost, pna: isPrivateNetworkAccess(apiUrl), status: undefined, detail: undefined };

  if (backendErrors && backendErrors.length > 0) {
    const message =
      (backendErrors[0] && (backendErrors[0].ErrorMessage || backendErrors[0].errorMessage)) ||
      undefined;
    return { ...base, code: ERROR_CODES.BACKEND_ERROR, detail: message };
  }

  if (response && !response.ok) {
    return {
      ...base,
      code: ERROR_CODES.HTTP_ERROR,
      status: response.status,
      detail: response.statusText,
    };
  }

  if (error) {
    if (isOffline()) {
      return { ...base, code: ERROR_CODES.OFFLINE, detail: error.message };
    }
    if (isMixedContent(apiUrl)) {
      return { ...base, code: ERROR_CODES.MIXED_CONTENT, detail: error.message };
    }
    if (crossHost) {
      return { ...base, code: ERROR_CODES.CROSS_HOST_BLOCKED, detail: error.message };
    }
    return { ...base, code: ERROR_CODES.UNREACHABLE, detail: error.message };
  }

  return { ...base, code: ERROR_CODES.UNKNOWN };
};

// Performs a fetch and returns a normalized result:
//   { ok: true, data }            on a successful 2xx response
//   { ok: false, error }          on transport failure or non-ok status,
//                                  where error is a classified error object.
export const request = async (url) => {
  const apiUrl = url.toString();
  let response;
  try {
    response = await fetch(url);
  } catch (e) {
    return { ok: false, error: classifyError({ error: e, apiUrl }) };
  }

  if (!response.ok) {
    return { ok: false, error: classifyError({ response, apiUrl }) };
  }

  let data;
  try {
    data = await response.json();
  } catch (e) {
    return { ok: false, error: classifyError({ error: e, apiUrl }) };
  }

  return { ok: true, data };
};
